import { useEffect, useState } from "react";
import api from "@/lib/api";
import { rupiah, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { confirmDialog } from "@/lib/confirm";
import { Download, FileText, FileSpreadsheet, FileType2, Send, Loader2, Info, ChevronRight, ChevronLeft, Link2, Copy, Check, RefreshCw, Trash2, KeyRound, Lock, Unlock, ShieldCheck, AlertCircle, CalendarDays, CheckCircle2 } from "lucide-react";

const PERIODS = [
  { value: "today", label: "Hari Ini" },
  { value: "week", label: "7 Hari" },
  { value: "month", label: "30 Hari" },
  { value: "year", label: "1 Tahun" },
];

const SECRET_SESSION_KEY = "rpt_secret_unlocked";

export default function Reports() {
  const [tab, setTab] = useState("ringkasan");
  const { user } = useAuth();
  const canShare = user && (user.role === "Admin" || user.role === "Bos");

  // ==== Fitur rahasia (hanya di akun ini; tidak ada di laporan Bang Billah) ====
  const [secretUnlocked, setSecretUnlocked] = useState(() => sessionStorage.getItem(SECRET_SESSION_KEY) === "1");
  const [pwDialog, setPwDialog] = useState(false);
  const [pw, setPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwErr, setPwErr] = useState("");

  const openSecret = () => { setPw(""); setPwErr(""); setPwDialog(true); };
  const submitPw = async () => {
    setPwBusy(true); setPwErr("");
    try {
      await api.post("/auth/verify-password", { password: pw });
      sessionStorage.setItem(SECRET_SESSION_KEY, "1");
      setSecretUnlocked(true);
      setPwDialog(false);
      toast.success("Mode lanjutan aktif");
    } catch {
      setPwErr("Password salah.");
    } finally { setPwBusy(false); }
  };
  const lockSecret = () => {
    sessionStorage.removeItem(SECRET_SESSION_KEY);
    setSecretUnlocked(false);
    setPwDialog(false);
    toast.success("Mode lanjutan dikunci");
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          {/* Trigger tersamarkan: klik teks kecil ini untuk membuka mode lanjutan */}
          <p
            data-testid="secret-trigger"
            onClick={openSecret}
            className="text-xs uppercase tracking-widest text-muted-foreground font-semibold select-none"
          >
            Ringkasan Keuangan
          </p>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Laporan</h1>
        </div>
      </div>

      {canShare && <ShareLinkCard />}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger data-testid="tab-ringkasan" value="ringkasan">Ringkasan</TabsTrigger>
          <TabsTrigger data-testid="tab-rekap-bulanan" value="rekap-bulanan">Rekap Bulanan</TabsTrigger>
        </TabsList>

        <TabsContent value="ringkasan" className="mt-4"><RingkasanTab /></TabsContent>
        <TabsContent value="rekap-bulanan" className="mt-4"><RekapBulananTab secretUnlocked={secretUnlocked} /></TabsContent>
      </Tabs>

      {/* Dialog password / status mode lanjutan */}
      <Dialog open={pwDialog} onOpenChange={(o) => { if (!o) setPwDialog(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {secretUnlocked ? <ShieldCheck className="h-4 w-4 text-emerald-600" /> : <Lock className="h-4 w-4" />}
              {secretUnlocked ? "Mode Lanjutan Aktif" : "Verifikasi"}
            </DialogTitle>
          </DialogHeader>
          {secretUnlocked ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Mode lanjutan sedang aktif untuk sesi ini.</p>
              <Button data-testid="btn-lock-secret" variant="outline" className="w-full gap-2" onClick={lockSecret}>
                <Lock className="h-4 w-4" /> Kunci lagi
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <Input
                data-testid="secret-pw-input"
                type="password"
                autoFocus
                placeholder="Masukkan password akun"
                value={pw}
                onChange={(e) => { setPw(e.target.value); setPwErr(""); }}
                onKeyDown={(e) => { if (e.key === "Enter" && pw) submitPw(); }}
              />
              {pwErr && <p className="text-xs text-rose-600 flex items-center gap-1.5"><AlertCircle className="h-3.5 w-3.5" /> {pwErr}</p>}
              <DialogFooter>
                <Button data-testid="btn-submit-secret" onClick={submitPw} disabled={pwBusy || !pw} className="w-full gap-2">
                  {pwBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlock className="h-4 w-4" />} Buka
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RingkasanTab() {
  const [period, setPeriod] = useState("today");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/reports/summary", { params: { period } }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [period]);

  const download = async (kind) => {
    try {
      const r = await api.get(`/reports/export/${kind}`, { params: { period }, responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `laporan_${period}.${kind === "excel" ? "xlsx" : kind}`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Laporan ${kind.toUpperCase()} diunduh`);
    } catch { /* ignore download error */ }
  };
  const sendToBoss = async () => {
    setSending(true);
    try {
      const r = await api.post("/reports/send-wa", { period, base_url: window.location.origin });
      if (r.data.sent) toast.success("Pemberitahuan laporan baru terkirim ke Bos ✓");
      else if (r.data.wa_link) { window.open(r.data.wa_link, "_blank"); toast.success("Membuka WhatsApp..."); }
    } finally { setSending(false); }
  };

  const s = data?.summary;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Tabs value={period} onValueChange={setPeriod}>
          <TabsList>
            {PERIODS.map((p) => <TabsTrigger key={p.value} value={p.value} data-testid={`period-${p.value}`}>{p.label}</TabsTrigger>)}
          </TabsList>
        </Tabs>
        <Button data-testid="btn-send-boss" onClick={sendToBoss} disabled={sending} className="gap-2">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Kirim ke Bos
        </Button>
      </div>
      {loading || !data ? <Skeleton className="h-64 w-full" /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatBox label="Jumlah Transaksi" value={s.jumlah_transaksi} isNumber />
            <StatBox label="Omzet" value={s.omzet} />
            <StatBox label="Keuntungan" value={s.keuntungan} tone="success" />
            <StatBox label="Biaya Admin" value={s.biaya_admin} />
            <StatBox label="Pengeluaran" value={s.pengeluaran} tone="danger" />
            <StatBox label="Laba Bersih" value={s.laba_bersih} tone={s.laba_bersih >= 0 ? "success" : "danger"} bold />
            <StatBox label="Piutang Aktif" value={s.piutang_aktif} tone="danger" />
            <StatBox label="Total Saldo Apps" value={s.total_saldo_apps} />
          </div>
          <Card className="p-5">
            <p className="font-semibold flex items-center gap-2"><Download className="h-4 w-4 text-primary" /> Ekspor Laporan</p>
            <div className="mt-4 grid grid-cols-3 gap-3">
              <Button data-testid="btn-export-pdf" variant="outline" onClick={() => download("pdf")} className="h-16 flex-col gap-1.5">
                <FileText className="h-5 w-5 text-rose-600" /><span className="text-xs font-semibold">PDF</span>
              </Button>
              <Button data-testid="btn-export-excel" variant="outline" onClick={() => download("excel")} className="h-16 flex-col gap-1.5">
                <FileSpreadsheet className="h-5 w-5 text-emerald-600" /><span className="text-xs font-semibold">Excel</span>
              </Button>
              <Button data-testid="btn-export-csv" variant="outline" onClick={() => download("csv")} className="h-16 flex-col gap-1.5">
                <FileType2 className="h-5 w-5 text-blue-600" /><span className="text-xs font-semibold">CSV</span>
              </Button>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

const BULAN_ID_R = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const _curMonthStr = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`; };
const _monthLabel = (m) => { const [y, mo] = m.split("-").map(Number); return `${BULAN_ID_R[mo - 1]} ${y}`; };
const _shiftMonth = (m, delta) => { const [y, mo] = m.split("-").map(Number); const d = new Date(y, mo - 1 + delta, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };

// ===== Rekap Bulanan: tabel rekap harian (dominan) + panel Rekapitulasi (secukupnya) =====
function RekapBulananTab({ secretUnlocked }) {
  const [month, setMonth] = useState(_curMonthStr());
  const [data, setData] = useState(null);   // daily-breakdown
  const [rekap, setRekap] = useState(null); // rekapitulasi (item saldo lifetime)
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null); // { label, items }
  const [selectedDay, setSelectedDay] = useState(null);
  const [secretMonths, setSecretMonths] = useState(() => new Set());

  const loadData = (mo) => {
    setLoading(true);
    return Promise.all([
      api.get("/reports/daily-breakdown", { params: { month: mo } }),
      api.get("/reports/rekapitulasi", { params: { period: "year" } }),
    ]).then(([d, r]) => { setData(d.data); setRekap(r.data); }).finally(() => setLoading(false));
  };

  useEffect(() => { loadData(month); }, [month]);

  // Daftar bulan aktif dari server (untuk label toggle), dimuat sekali saat mount.
  useEffect(() => {
    api.get("/settings/secret-formula-months")
      .then((r) => setSecretMonths(new Set(r.data.months || [])))
      .catch(() => { /* non-admin / gagal: abaikan */ });
  }, []);

  const atCurrent = month >= _curMonthStr();
  const days = data?.days || [];
  const personalNames = data?.personal_names || [];
  const modal = rekap?.modal || 0;
  // Tampilan rumus akumulatif ditentukan backend (permanen; juga berlaku utk laporan Bang Billah).
  const secretData = !!data?.secret_formula;
  // Status yang diinginkan utk bulan ini (kontrol toggle butuh mode lanjutan terbuka).
  const secretWanted = secretMonths.has(month);

  const toggleMonthSecret = () => {
    const prev = secretMonths;
    const next = new Set(prev);
    if (next.has(month)) next.delete(month); else next.add(month);
    setSecretMonths(next); // optimistik utk label
    api.put("/settings/secret-formula-months", { months: [...next] })
      .then((r) => {
        setSecretMonths(new Set(r.data.months || []));
        loadData(month); // muat ulang agar tabel memakai hasil rumus dari backend
      })
      .catch(() => {
        setSecretMonths(prev); // revert
        toast.error("Gagal menyimpan pilihan bulan ke server");
      });
  };

  const personalSum = (d, name) => (d.pinjaman_personal?.[name] || []).reduce((s, x) => s + x.amount, 0);

  // TOTAL baris paling bawah
  const totAll = {
    pemasukan: days.reduce((s, d) => s + (d.pemasukan || 0), 0),
    pengeluaran: days.reduce((s, d) => s + (d.pengeluaran || 0), 0),
    untung: days.reduce((s, d) => s + (d.untung || 0), 0),
    personal: Object.fromEntries(personalNames.map((n) => [n, days.reduce((s, d) => s + personalSum(d, n), 0)])),
    pinjaman_orang: days.reduce((s, d) => s + (d.total_pinjaman_orang || 0), 0),
    sisa_saldo: days.length ? days[days.length - 1].sisa_saldo : 0,
  };

  const totalCols = 6 + personalNames.length + 2;
  // Rekapitulasi SELALU bentuk harian tunggal. Default: hari terakhir. Klik baris hanya ganti isi, bentuk tetap.
  const selectDay = (key) => setSelectedDay(key);
  const dayObj = (selectedDay && days.find((d) => d.tanggal === selectedDay)) || (days.length ? days[days.length - 1] : null);
  const activeKey = dayObj?.tanggal;

  return (
    <div className="space-y-4">
      {/* Switcher bulan */}
      <div className="flex items-center justify-center gap-2">
        <Button data-testid="month-prev" variant="outline" size="icon" className="h-9 w-9" onClick={() => setMonth((m) => _shiftMonth(m, -1))}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div data-testid="month-label" className="min-w-[160px] text-center font-semibold text-base">{_monthLabel(month)}</div>
        <Button data-testid="month-next" variant="outline" size="icon" className="h-9 w-9" disabled={atCurrent} onClick={() => setMonth((m) => _shiftMonth(m, 1))}>
          <ChevronRight className="h-4 w-4" />
        </Button>
        {!atCurrent && (
          <Button data-testid="month-today" variant="ghost" size="sm" className="ml-1 text-xs" onClick={() => setMonth(_curMonthStr())}>Bulan ini</Button>
        )}
      </div>

      {/* Kontrol rumus rahasia (hanya tampil bila mode lanjutan terbuka) */}
      {secretUnlocked && (
        <div className="flex items-center justify-center">
          <button
            data-testid="btn-toggle-secret-month"
            onClick={toggleMonthSecret}
            className={`inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${secretWanted ? "bg-primary/10 border-primary/40 text-primary" : "bg-muted/40 border-border text-muted-foreground hover:bg-muted"}`}
          >
            {secretWanted ? <ShieldCheck className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
            Rumus akumulatif {secretWanted ? "AKTIF" : "nonaktif"} · {_monthLabel(month)}
          </button>
        </div>
      )}

      {loading || !data || !rekap ? <Skeleton className="h-96 w-full" /> : (
        <div className="flex flex-col xl:flex-row gap-4 items-start">
          {/* Tabel rekap bulanan — DOMINAN */}
          <Card className="w-full xl:flex-1 xl:min-w-0 overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground border-b">
                <tr>
                  <th className="text-left px-3 py-2.5 font-semibold">Tgl</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Hari</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Modal</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Pemasukan</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Pengeluaran</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Untung</th>
                  {personalNames.map((n) => (
                    <th key={n} className="text-right px-3 py-2.5 font-semibold">{n}{secretData ? " (akm)" : ""}</th>
                  ))}
                  <th className="text-right px-3 py-2.5 font-semibold">Pinjaman Orang{secretData ? " (akm)" : ""}</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Sisa Saldo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {days.map((d) => {
                  const isSel = d.tanggal === activeKey;
                  const rowBase = d.is_weekend ? "bg-amber-50/70 dark:bg-amber-500/10 hover:bg-amber-100/70" : "hover:bg-accent/30";
                  const selCls = isSel ? "ring-2 ring-inset ring-primary/60 bg-primary/5" : "";
                  const untungVal = d.untung; // untung selalu harian (tidak akumulatif)
                  const sisaVal = d.sisa_saldo; // sisa saldo dari backend (sudah hasil rumus bila bulan diaktifkan)
                  return (
                    <tr
                      key={d.tanggal}
                      data-testid={`daily-row-${d.tanggal}`}
                      onClick={() => selectDay(d.tanggal)}
                      title="Klik untuk lihat rekapitulasi tanggal ini"
                      className={`cursor-pointer ${rowBase} ${selCls}`}
                    >
                      <td className="px-3 py-2 whitespace-nowrap font-medium">
                        <span className="inline-flex items-center gap-1.5">
                          {isSel && <CheckCircle2 className="h-3.5 w-3.5 text-primary" />}
                          {d.label || d.tanggal.slice(5)}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {d.is_weekend
                          ? <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400 font-medium">{d.hari}<span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-200/70 dark:bg-amber-500/20">1 hari kerja</span></span>
                          : d.hari}
                      </td>
                      <td className="px-3 py-2 text-right money text-muted-foreground">{rupiah(d.modal)}</td>
                      <td className="px-3 py-2 text-right money text-emerald-600 dark:text-emerald-400">{d.pemasukan ? rupiah(d.pemasukan) : "-"}</td>
                      <td className="px-3 py-2 text-right money text-rose-600 dark:text-rose-400">{d.pengeluaran ? rupiah(d.pengeluaran) : "-"}</td>
                      <td className={`px-3 py-2 text-right money font-semibold ${untungVal >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                        {rupiah(untungVal)}
                      </td>
                      {personalNames.map((n) => {
                        if (secretData) {
                          const v = d.personal_cum?.[n] || 0;
                          return <td key={n} className="px-3 py-2 text-right money">{v ? rupiah(v) : <span className="text-muted-foreground">-</span>}</td>;
                        }
                        const items = d.pinjaman_personal?.[n] || [];
                        const sum = items.reduce((s, x) => s + x.amount, 0);
                        return (
                          <td key={n} className="px-3 py-2 text-right">
                            {sum > 0 ? (
                              <button
                                data-testid={`personal-${n}-${d.tanggal}`}
                                className="money font-semibold text-primary hover:underline inline-flex items-center gap-1"
                                onClick={(e) => { e.stopPropagation(); setDetail({ label: `${n} · ${d.tanggal}`, items }); }}
                              >
                                {rupiah(sum)}
                                <ChevronRight className="h-3 w-3" />
                              </button>
                            ) : <span className="text-muted-foreground">-</span>}
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 text-right">
                        {secretData ? (
                          <span className="money">{d.orang_cum ? rupiah(d.orang_cum) : <span className="text-muted-foreground">-</span>}</span>
                        ) : d.total_pinjaman_orang > 0 ? (
                          <button
                            data-testid={`orang-${d.tanggal}`}
                            className="money font-semibold text-primary hover:underline inline-flex items-center gap-1"
                            onClick={(e) => { e.stopPropagation(); setDetail({ label: `Pinjaman Orang · ${d.tanggal}`, items: d.pinjaman_orang }); }}
                          >
                            {rupiah(d.total_pinjaman_orang)}
                            <ChevronRight className="h-3 w-3" />
                          </button>
                        ) : <span className="text-muted-foreground">-</span>}
                      </td>
                      <td className="px-3 py-2 text-right money font-semibold">{rupiah(sisaVal)}</td>
                    </tr>
                  );
                })}
                {days.length === 0 && (
                  <tr><td colSpan={totalCols} className="text-center text-sm text-muted-foreground py-10">Belum ada data bulan ini.</td></tr>
                )}
              </tbody>
              {days.length > 0 && (
                <tfoot>
                  <tr data-testid="daily-total-row" className="bg-muted/70 border-t-2 border-border font-semibold">
                    <td className="px-3 py-2.5 text-left uppercase tracking-wide text-xs" colSpan={2}>Total</td>
                    <td className="px-3 py-2.5 text-right text-muted-foreground">—</td>
                    <td className="px-3 py-2.5 text-right money text-emerald-700 dark:text-emerald-400">{rupiah(totAll.pemasukan)}</td>
                    <td className="px-3 py-2.5 text-right money text-rose-700 dark:text-rose-400">{rupiah(totAll.pengeluaran)}</td>
                    <td className={`px-3 py-2.5 text-right money ${totAll.untung >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{rupiah(totAll.untung)}</td>
                    {personalNames.map((n) => (
                      <td key={n} className="px-3 py-2.5 text-right money">{totAll.personal[n] > 0 ? rupiah(totAll.personal[n]) : "-"}</td>
                    ))}
                    <td className="px-3 py-2.5 text-right money">{totAll.pinjaman_orang > 0 ? rupiah(totAll.pinjaman_orang) : "-"}</td>
                    <td className="px-3 py-2.5 text-right money">{rupiah(totAll.sisa_saldo)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </Card>

          {/* Panel Rekapitulasi — SATU bentuk HARIAN tunggal; klik baris hanya ganti isi, bentuk tetap */}
          <Card className="w-full xl:w-[330px] xl:shrink-0 p-4 space-y-3" data-testid="rekap-panel">
            <div className="min-w-0">
              <p className="font-semibold text-sm">Rekapitulasi</p>
              <p data-testid="rekap-scope" className="text-[11px] text-muted-foreground truncate">
                {dayObj ? <>{dayObj.label} · {dayObj.hari}</> : _monthLabel(month)}
              </p>
            </div>

            {dayObj
              ? <DayRekap day={dayObj} personalNames={personalNames} modal={modal} personalSum={personalSum} />
              : <p className="text-xs text-muted-foreground py-6 text-center">Belum ada data bulan ini.</p>}
          </Card>
        </div>
      )}

      <Card className="p-4 bg-muted/40 border-dashed">
        <p className="text-xs text-muted-foreground flex items-start gap-2">
          <Info className="h-3.5 w-3.5 mt-0.5" />
          <span>
            {secretData ? (
              <>
                <strong>Rumus akumulatif aktif untuk bulan ini:</strong> kolom Pinjaman menjadi akumulatif, dan
                <strong> Sisa Saldo = Modal + Untung harian − Pinjaman akumulatif</strong>. Berlaku permanen (juga di laporan Bang Billah) selama bulan ini diaktifkan.
              </>
            ) : (
              <>
                <strong>Sisa Saldo</strong> = Modal + akumulasi Untung − akumulasi Pinjaman Outstanding.
                Baris <strong>Total</strong> menjumlahkan seluruh item bulan ini. <strong>Klik baris tanggal</strong> untuk melihat Rekapitulasi hari tersebut.
              </>
            )}
          </span>
        </p>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => (o ? null : setDetail(null))}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{detail?.label}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {(detail?.items || []).map((it) => (
              <Card key={it.id} className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm">{it.customer_name}</p>
                    {it.phone && <p className="text-xs text-muted-foreground">{it.phone}</p>}
                    {it.due_date && <p className="text-xs text-muted-foreground">Jatuh tempo: {formatDate(it.due_date)}</p>}
                    {it.notes && <p className="text-xs text-muted-foreground italic mt-1">{`"${it.notes}"`}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="money font-bold">{rupiah(it.amount)}</p>
                    <Badge variant={it.remaining > 0 ? "destructive" : "default"} className="text-[10px]">
                      {it.remaining > 0 ? `Sisa ${rupiah(it.remaining)}` : "Lunas"}
                    </Badge>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Baris item ringkas untuk panel Rekapitulasi
function RekapRow({ kode, item, nilai, note, tone }) {
  const toneCls = tone === "success" ? "border-emerald-500/40 bg-emerald-500/5" : tone === "warn" ? "border-amber-500/40 bg-amber-500/5" : "border-border";
  const valColor = tone === "success" ? "text-emerald-700 dark:text-emerald-400" : tone === "warn" ? "text-amber-700 dark:text-amber-400" : "";
  return (
    <div data-testid={`rekap-row-${kode}`} className={`flex items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 ${toneCls}`}>
      <div className="min-w-0">
        <p className="font-medium text-xs truncate">{item}</p>
        {note && <p className="text-[10px] text-muted-foreground mt-0.5">{note}</p>}
      </div>
      <p className={`money font-bold text-xs text-right shrink-0 ${valColor}`}>{rupiah(nilai)}</p>
    </div>
  );
}

function Sec({ children }) {
  return <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground pt-2 pb-0.5">{children}</div>;
}

// Rekapitulasi HARIAN tunggal (bentuk sama untuk semua bulan; TIDAK dipengaruhi fitur rumus).
// Membaca nilai apa adanya dari data tabel (day.*), termasuk sisa_saldo yang sudah dari backend.
function DayRekap({ day, personalNames, modal, personalSum }) {
  const selisih_saldo = (day.sisa_saldo || 0) - (modal || 0);
  return (
    <div className="space-y-1.5">
      <Sec>Keuntungan</Sec>
      <RekapRow kode="H" item="Pemasukan" nilai={day.pemasukan || 0} />
      <RekapRow kode="I" item="Pengeluaran" nilai={day.pengeluaran || 0} />
      <RekapRow kode="J" item="Untung" nilai={day.untung || 0} tone="success" note="H − I" />
      <Sec>Saldo</Sec>
      <RekapRow kode="A" item="Modal" nilai={modal || 0} />
      <RekapRow kode="D" item="Sisa Saldo" nilai={day.sisa_saldo || 0} tone="success" />
      <RekapRow kode="G" item="Selisih Saldo" nilai={selisih_saldo} note="Sisa Saldo − Modal" />
      <Sec>Pinjaman</Sec>
      {personalNames.map((n) => (
        <RekapRow key={n} item={n} nilai={personalSum(day, n)} />
      ))}
      <RekapRow item="Pinjaman Orang" nilai={day.total_pinjaman_orang || 0} />
    </div>
  );
}

function ShareLinkCard() {
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/reports/share-link").then((r) => setLink(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const shareUrl = link?.token ? `${window.location.origin}/laporan/${link.token}` : "";

  const create = async () => {
    setBusy(true);
    try { await api.post("/reports/share-link"); toast.success("Link untuk Bang Billah dibuat"); load(); }
    finally { setBusy(false); }
  };
  const regenerate = async () => {
    if (!(await confirmDialog({ title: "Perbarui link?", description: "Link lama berhenti berfungsi dan Bang Billah harus membuat PIN baru.", confirmText: "Perbarui", destructive: false }))) return;
    setBusy(true);
    try { await api.post("/reports/share-link"); toast.success("Link diperbarui. Kirim link baru ke Bang Billah."); load(); }
    finally { setBusy(false); }
  };
  const resetPin = async () => {
    if (!(await confirmDialog({ title: "Reset PIN?", description: "Bang Billah akan diminta membuat PIN baru saat membuka link.", confirmText: "Reset PIN", destructive: false }))) return;
    setBusy(true);
    try { await api.post("/reports/share-link/reset-pin"); toast.success("PIN direset"); load(); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!(await confirmDialog({ title: "Cabut link?", description: "Bang Billah tidak bisa mengakses laporan lagi.", confirmText: "Cabut" }))) return;
    setBusy(true);
    try { await api.delete("/reports/share-link"); toast.success("Link dinonaktifkan"); load(); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true); toast.success("Link disalin");
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Gagal menyalin"); }
  };

  if (loading) return <Skeleton className="h-28 w-full" />;

  return (
    <Card className="p-5 border-primary/30 bg-primary/[0.03]" data-testid="share-link-card">
      <div className="flex items-center gap-2.5">
        <div className="h-9 w-9 rounded-lg bg-primary/10 grid place-items-center shrink-0">
          <Link2 className="h-4 w-4 text-primary" />
        </div>
        <div>
          <p className="font-semibold text-sm">Link Laporan untuk Bang Billah</p>
          <p className="text-xs text-muted-foreground">Akses read-only & terkunci PIN. Tanpa akses ke menu lain.</p>
        </div>
      </div>

      {!link?.exists || !link?.active ? (
        <div className="mt-4">
          {link?.exists && !link?.active && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mb-2 flex items-center gap-1.5">
              <AlertCircle className="h-3.5 w-3.5" /> Link saat ini dinonaktifkan.
            </p>
          )}
          <Button data-testid="btn-create-share-link" onClick={create} disabled={busy} className="gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
            Buat Link untuk Bang Billah
          </Button>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-2">
            <Input data-testid="share-link-url" readOnly value={shareUrl} className="font-mono text-xs" onFocus={(e) => e.target.select()} />
            <Button data-testid="btn-copy-share-link" variant="outline" size="icon" onClick={copy} className="shrink-0">
              {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <div>
            {link.pin_set ? (
              <Badge variant="default" className="gap-1"><ShieldCheck className="h-3 w-3" /> PIN sudah dibuat</Badge>
            ) : (
              <Badge variant="secondary" className="gap-1"><Lock className="h-3 w-3" /> Menunggu Bang Billah membuat PIN</Badge>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap pt-1">
            <Button data-testid="btn-regenerate-share-link" variant="outline" size="sm" onClick={regenerate} disabled={busy} className="gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" /> Perbarui Link
            </Button>
            <Button data-testid="btn-reset-share-pin" variant="outline" size="sm" onClick={resetPin} disabled={busy || !link.pin_set} className="gap-1.5">
              <KeyRound className="h-3.5 w-3.5" /> Reset PIN
            </Button>
            <Button data-testid="btn-revoke-share-link" variant="outline" size="sm" onClick={revoke} disabled={busy} className="gap-1.5 text-rose-600 hover:text-rose-600">
              <Trash2 className="h-3.5 w-3.5" /> Cabut
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function StatBox({ label, value, isNumber, tone, bold }) {
  const colors = { success: "text-emerald-600 dark:text-emerald-400", danger: "text-rose-600 dark:text-rose-400" };
  return (
    <Card className="p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium">{label}</p>
      <p className={`mt-1.5 money ${bold ? "text-2xl font-bold" : "text-xl font-semibold"} tracking-tight ${colors[tone] || ""}`}>
        {isNumber ? value : rupiah(value)}
      </p>
    </Card>
  );
}
